import React, { useState, useEffect } from 'react';
import { useKey } from '../hooks/useKey';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Box, Button, Typography, Paper, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import SimpleModal from '../components/SimpleModal';
import Header from '../components/Header';
import Banner from '../components/Banner';
import DocumentManager from './DocumentManager';
// import LogoutIcon from '@mui/icons-material/Logout';

//http://localhost:5173/documents?key=AQICAHjpdfHDsL7FDrkfJqMWFxlzqTFKALiA1C4ezyZ3KcOG8gE5nt466E9UW-8XVfDdK3kQAAAAkjCBjwYJKoZIhvcNAQcGoIGBMH8CAQAwegYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAxcgLlxpfWt1xuEW2QCARCATYHeMij5eoFhFoHhQChrvT1AVxlWjRAPDlNFKbGrEKt-daCZYn2zgRL2AooL7B1rTqZrJ4gqOIkyRNqelIoDDCrFy8JLzjFv4GLysgQb
const AuthPage = () => {
  const [isKeyActivated, setIsKeyActivated] = useState(true);
  const [errorMessage, setErrorMessage] = useState('SESSION NOT STARTED');
  const navigate = useNavigate();
  const [sessionEndTime, setSessionEndTime] = useState(null);
  const [showSessionEndModal, setShowSessionEndModal] = useState(false);
  const [propertyName, setPropertyName] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [hideHeader, setHideHeader] = useState(false);
  const { key } = useKey();

  // console.log("KEY", key);

  const handleSessionEndTime = (endTime) => {
    setSessionEndTime(endTime);
    
    const msUntilEnd = dayjs(endTime).diff(dayjs());
    
    if (msUntilEnd > 0) {
      setTimeout(() => {
        setShowSessionEndModal(true);
      }, msUntilEnd);
    }
  };

  const listDocumentsMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/list-documents`, {
        headers: {
          'X-Signature': key
        }
      });
      const data = await response.json();
      // console.log("LIST RESPONSE:", data);

      if (response.status !== 200) {
        setErrorMessage(data.message);
        if (data.errorCode === "ERR_SESSION_NOT_STARTED") {
          setIsKeyActivated(false);
        }
        return null;
      }
      handleSessionEndTime(dayjs(data.sessionEnd));
      setPropertyName(data.alfrescoRootName.split('-')[1]);
      return {
        ...data.data,
        entries: data.data.entries.sort((a, b) => a.name.localeCompare(b.name))
      };
    },
    onSuccess: (data) => {
      setDocuments(data);
    },
    onError: (error) => {
      console.error("Error fetching documents:", error);
    }
  });

  const startSessionMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/start-session`, {
        method: 'POST',
        headers: {
          'X-Signature': key
        }
      });
      const data = await response.json();
      if (response.status !== 200) {
        setErrorMessage(data.message);
        return null;
      }
      handleSessionEndTime(dayjs(data.sessionEnd));
      listDocumentsMutation.mutate();
    },
    onSuccess: (data) => {
      // Handle successful session start if needed
      console.log("Session started successfully", data);
      setIsKeyActivated(true);
    },
    onError: (error) => {
      console.error("Error starting session:", error);
    }
  });

  // First attempt to list documents
  useEffect(() => {
    if (key && key.length > 0) {
      listDocumentsMutation.mutate();
    }
  }, [key]);

  const startSession = () => {
    console.log("Activating key");
    startSessionMutation.mutate();
  }

  return (
    <Box sx={{
      bgcolor: 'white', 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      overflow: 'hidden',
      maxHeight: '100vh'
    }}>
      {!hideHeader && <Header displayAll={false}/>}
      {!hideHeader && <Banner displayAll={false} closeBanner={!!documents}/>} 
      {!hideHeader && <Box sx={{
          position: 'sticky',
          top: 148,
          width: '100%',
          maxWidth: '100vw',
          bgcolor: 'secondary.main',
          p: 2,
          zIndex: 1000,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
            <Typography align="center" color="white" variant="h5" noWrap>
              {sessionEndTime ? `Session ends at ${dayjs(sessionEndTime).format('DD/MM/YYYY hh:mm A')}` : errorMessage.includes("Start Session") ? "SESSION NOT STARTED" : errorMessage.length > 50 ? "Error" : errorMessage.toUpperCase()}
            </Typography>
        </Box>}



      {documents ?
        <DocumentManager 
          documents={documents} 
          accessKey={key} 
          sessionEndTime={sessionEndTime} 
          propertyName={propertyName} 
          hideHeader={hideHeader}
          toggleHeader={(value) => setHideHeader(value)}
        />
        :
        <>
          {errorMessage && (<><Typography variant="h6" align="center" color="primary" sx={{ marginBottom: '20px' }}>{errorMessage}</Typography></>)}
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
            {/* <Button sx={{ color: 'white' }} color='secondary' variant="contained" aria-label="Navigate to Home" onClick={() => navigate('/')}>HOME</Button> */}
            {(key && !isKeyActivated) && <Button sx={{ color: 'white' }} color='secondary' variant="contained" aria-label="Activate Key" onClick={() => startSession()}>Start Session</Button>}
          </Stack>
        </>
      }
      <SimpleModal inProgress={startSessionMutation.isPending} text={"Starting session..."} />
      <SimpleModal inProgress={listDocumentsMutation.isPending} text={"Loading..."} />
      <SimpleModal 
        inProgress={showSessionEndModal} 
        text={"Your scheduled access has now run out. You will now be redirected to the access request form."} 
        buttonFunction={() => navigate('/')}
      />
    </Box>
  );
}

export default AuthPage;
